/**
 * The words on screen, and the one function that resolves them.
 *
 * THE DEFAULT LIVES AT THE CALL SITE, which is the decision the rest of this
 * follows from:
 *
 *   t('sponsors.col.status', 'Status')
 *
 * So the application has all of its words with no database, no network call
 * and no catalogue file. An override is an improvement on what is already
 * there, never a dependency of it. A failed fetch, an empty table or a brand
 * new install all render exactly what shipped, and there is no state in which a
 * screen comes up blank because its text did not arrive.
 *
 * WHERE OVERRIDES COME FROM. The server injects them into the page shell
 * before the bundle runs, as `window.__COPY__`. That is deliberate rather than
 * a second fetch: overridden text would otherwise appear as the shipped
 * wording for a frame and then change under the reader, which is exactly how
 * this kind of feature announces itself to the people it is meant to be
 * invisible to. An inline classic script executes during parsing, while the
 * bundle is a module and therefore deferred, so the values are always in place
 * by the time anything renders.
 *
 * KEYS ARE LITERALS, ALWAYS. The catalogue is built by reading these calls out
 * of the source, so a key assembled at runtime is invisible to the editor and
 * uneditable forever. The extraction script refuses to build rather than skip
 * one; see server/src/scripts/extractCopy.ts.
 */

declare global {
  interface Window {
    /** Resolved overrides, injected into the shell. Absent in development and
     *  on any deployment where nobody has edited anything. */
    __COPY__?: Record<string, string>;
  }
}

/**
 * Reads the overrides the server put into the page as a JSON data block.
 *
 * NOT AN INLINE SCRIPT ANY MORE. The shell used to set `window.__COPY__` from
 * an inline `<script>`, which the Content-Security-Policy blocks, so no edited
 * wording ever reached a screen. A `type="application/json"` block is never
 * executed, so CSP leaves it alone; it is read here once, when this module
 * first loads, which is before anything renders. `window.__COPY__` stays the
 * in-memory store because the editor writes to it after a save.
 */
function readInjected(): Record<string, string> | undefined {
  try {
    const el = document.getElementById('copy-overrides');
    if (!el?.textContent) return undefined;
    const parsed: unknown = JSON.parse(el.textContent);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : undefined;
  } catch {
    /* A malformed block means shipped wording, never a blank screen. */
    return undefined;
  }
}

if (typeof window !== 'undefined' && window.__COPY__ === undefined) {
  const injected = readInjected();
  if (injected) window.__COPY__ = injected;
}

/**
 * The wording for `key`, or `fallback` when nobody has changed it.
 *
 * `vars` fills `{name}` placeholders. The server refuses to save an override
 * that drops one, so a placeholder reaching a reader as literal text is a bug
 * in this function rather than something an editor can cause.
 */
export function t(key: string, fallback: string, vars?: Record<string, string | number>): string {
  const raw = window.__COPY__?.[key] ?? fallback;
  if (!vars) return raw;

  let out = raw;
  for (const name of Object.keys(vars)) {
    out = out.split(`{${name}}`).join(String(vars[name]));
  }
  return out;
}

/** Whether this string is currently showing something other than what shipped.
 *  Only the editor cares; it is what paints the "changed" outline. */
export function isOverridden(key: string): boolean {
  return window.__COPY__ !== undefined && key in window.__COPY__;
}
